import { get, sortBy } from 'lodash';
import STEP_ROUTES from '../../../constants/proposal-step-routes';

import {
    LIVE_CAMPAIGN_PROPOSAL_SUBMIT_SUCCESS_EVENT,
    LIVE_CAMPAIGN_PROPOSAL_SUBMIT_ERROR_EVENT,
    LIVE_CAMPAIGN_PROPOSAL_STEP_EVENTS,
    ANALYTICS_PROPOSAL_STEP_EVENTS,
} from '../../../constants/analytics-events';

import ProposalsMixin from './ProposalsMixin';
import EventTrackingMixin from '../../../mixins/EventTrackingMixin';

const PROPOSAL_EXPANDS = 'expand[]=product_selection_items&expand[]=product_selection_items.product&expand[]=product_selection_items.variant&expand[]=product_selection_items.product.variants&expand[]=steps&expand[]=tasks&expand[]=tasks&expand[]=contact.networks.network&expand[]=contact.primary_address&expand[]=campaign.brand';

export default {
    mixins: [ProposalsMixin, EventTrackingMixin],
    computed: {
        proposalCompleteUrl() {
            return `/api/v1/contacts/${this.contactId}/campaigns/${this.campaignId}/proposals/${this.proposalId}/complete?${PROPOSAL_EXPANDS}`;
        },
        incompleteProposal() {
            const incompleteProposals = this.proposals.filter((proposal) => {
                return !proposal.is_complete;
            });

            return sortBy(incompleteProposals, ['created_at'])[incompleteProposals.length - 1]; // Get most recently created incomplete proposal.
        },
        proposalStepsOrder() {
            return get(this.$root.v2, 'proposal.steps', []);
        },
        proposalSteps() {
            const steps = get(this.$root.v2, 'proposal.steps', []);
            const totalSteps = steps.length;

            return steps.reduce((result, step, index) => {
                const meta = {
                    meta: {
                        stepNumber: index + 1,
                        totalSteps
                    }
                };

                result[step.name] = {
                    ...step,
                    ...meta
                };
                return result;
            }, {});
        },
        currentProposalStep() {
            const currentStep = get(this.$root.v2, 'proposal.steps', []).find((step) => !step.is_complete) || {};
            return this.proposalSteps[currentStep.name];
        },
        isProposalPreview() {
            return !!this.$route.query.preview;
        }
    },
    methods: {
        getProposalStepUrl(name, complete = false) {
            const tail = complete ? '/complete' : '';
            return `/api/v1/proposals/${this.$route.params.proposal_id}/steps/${name}${tail}`;
        },
        postProposalStep(data) {
            return this.$http.post(this.getProposalStepUrl(data.step), data.payload).then((response) => {
                this.updateStep(response.data);
            });
        },
        patchProposalStep(data) {
            this.isPatchingProposal = true;
            this.resetErrors();

            if (typeof this.updateInitialStepState === 'function') {
                this.updateInitialStepState();
            }

            return this.$http
                .patch(this.getProposalStepUrl(data.step, data.complete), data.payload)
                .then((response) => {
                    this.updateStep(response.data);
                    return this.loadProposal(true);
                })
                .catch(this.handleHttpError)
                .then(() => {
                    this.isPatchingProposal = false;
                });
        },
        initProposalStep(step) {
            if (step.is_started) {
                return Promise.resolve();
            }
            this.$bus.$emit(LIVE_CAMPAIGN_PROPOSAL_STEP_EVENTS[step.name].start);
            this.trackLiveUrlEvent(ANALYTICS_PROPOSAL_STEP_EVENTS[step.name].start, {
                campaign_id: step.campaign_id
            });
            return this.postProposalStep({
                step: step.name,
                payload: {}
            });
        },
        completeProposalStep(data) {
            data.complete = true;
            return this.patchProposalStep(data);
        },
        goNextStep(currentStep, payload) {
            const maxIndex = this.proposalStepsOrder.length - 1;
            const index = this.proposalStepsOrder.findIndex((step) => step.name === currentStep.name);
            const nextIndex = currentStep.is_complete ? maxIndex + 1 : index + 1;

            const nextStep = this.proposalStepsOrder[nextIndex];

            this.resetErrors();
            this.isCompletingProposal = true;

            if (!currentStep.is_complete) {
                this.trackLiveUrlEvent(ANALYTICS_PROPOSAL_STEP_EVENTS[currentStep.name].complete, {
                    campaign_id: currentStep.campaign_id
                });
            }

            return this.$http
                .patch(this.getProposalStepUrl(currentStep.name, true), payload)
                .then((response) => {
                    this.updateStep(response.data);
                    this.$bus.$emit(LIVE_CAMPAIGN_PROPOSAL_STEP_EVENTS[currentStep.name].complete);
                    return this.loadProposal(true);
                })
                .then(() => {
                    if (nextIndex <= maxIndex) {
                        return this.$router.push({ name: STEP_ROUTES[nextStep.name] });
                    }
                    return this.$router.push({ name: 'v2.campaign.proposal' });
                })
                .catch(this.handleHttpError)
                .then(() => {
                    this.isCompletingProposal = false;
                });
        },
        goPreviousStep(currentStep) {
            const index = this.proposalStepsOrder.findIndex((step) => step.name === currentStep.name);

            if (index <= 0) {
                return this.$router.push({ name: 'v2.campaign.proposal' });
            }

            const nextIndex = index - 1;
            const nextStep = this.proposalStepsOrder[nextIndex];
            return this.$router.push({ name: STEP_ROUTES[nextStep.name] });
        },
        updateStep(updatedStep) {
            // Clean up what we don't need
            delete updatedStep.proposal;
            delete updatedStep.campaign;

            const index = get(this.$root.v2, 'proposal.steps', []).findIndex((step) => step.name === updatedStep.name);

            if (index > -1) {
                this.$root.v2.proposal.steps.splice(index, 1, updatedStep);
            }
        },
        completeProposal() {
            this.resetErrors();
            this.isCompletingProposal = true;

            return this.$http
                .patch(this.proposalCompleteUrl)
                .then((response) => {
                    this.$root.v2.proposal = {
                        ...this.$root.v2.proposal,
                        ...response.data
                    };
                    this.$bus.$emit(LIVE_CAMPAIGN_PROPOSAL_SUBMIT_SUCCESS_EVENT);
                    this.trackEventProposalSubmitted(this.campaign.id, true);
                })
                .catch((error) => {
                    this.$bus.$emit(LIVE_CAMPAIGN_PROPOSAL_SUBMIT_ERROR_EVENT);
                    this.trackEventProposalSubmitted(this.campaign.id, false);
                    this.handleHttpError(error, 'proposal');
                })
                .then(() => {
                    this.isCompletingProposal = false;
                    this.loadProposals();
                });
        }
    }
};
